import * as React from 'react'
import * as ReactDOM from "react-dom";
import { Controlled as CodeMirror } from 'react-codemirror2'
import { Editor as CodeMirrorEditor } from 'codemirror';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { classNames } from 'react-scoped-css-loader';
import 'codemirror/addon/display/placeholder'
import 'codemirror/addon/search/searchcursor'
import 'codemirror/lib/codemirror.css'
import './PillInput.scss'

export type IGetValue = (val: string) => string
export interface IPillConfig {
    valueIndex: number,
    renderValue?: (value: string) => string | JSX.Element
    icon: string | IGetValue,
    backgroundColor: string | IGetValue,
    color: string | IGetValue,
}

type IPillConfiguration = { [type: string]: IPillConfig }

function hasValue(value: any, skipZero?: boolean) {

    if (skipZero && typeof value == "number" && value == 0) return true
    if (!value) return false
    switch (typeof value) {
        case 'string':
            return value?.trim().length > 0
        case 'number':
            return value > 0
        default:
            return true
    }
}

interface IPillInputProps {
    value: string,
    onChange: (value: string) => void,
    pillConfiguration: IPillConfiguration,
    onFocus?: (editor: CodeMirrorEditor, event: Event) => void
    onBlur?: () => void
    expressionMatcher?: RegExp,
    pillValuesSplitFn?: (value: string) => string[]; // [type, ...values]
    disableMultipleConfigurations?: boolean,
    placeholder?: string
}
interface IPillInputInstanceProps {
    focus: () => void,
    insertAtCursor: (value: string) => void
}

const PillInput: React.ForwardRefExoticComponent<React.RefAttributes<IPillInputInstanceProps> & IPillInputProps> = React.forwardRef((props, ref) => {
    const { value, onChange, pillConfiguration, onFocus, onBlur, expressionMatcher } = props
    const ExpressionRegex = expressionMatcher || /\#{(.*?)}/

    const nodesRef = React.useRef<any[]>([])
    const editorInstanceRef = React.useRef<any>(null)
    const isCursorAtEndRef = React.useRef(false)

    const inputContainerRef = React.useRef(null)

    React.useImperativeHandle(ref, () => ({
        focus: focusInput,
        insertAtCursor: insertAtCursor
    }))

    React.useEffect(() => {
        console.log('pill input value ', value);
    }, [value])


    function handleOnMount(editor: any) {
        editorInstanceRef.current = editor
        replaceExpressionsWithPlaceholders(editor, { line: 0, ch: 0 })
        // focusInput()
    }

    function handleBeforeChange(editor: any, data: any, value: string) {
        onChange(value)
    }

    function handleChange(editor: any, data: any, value: string) {
        const { from } = data
        const fromStartOfLine = { ...from, ch: 0 };
        replaceExpressionsWithPlaceholders(editor, fromStartOfLine)
    }

    function handleUpdate(editor: any) {
        let needsRefresh = false;
        nodesRef.current = nodesRef.current.filter((n: any) => {
            console.log('node ', n);

            if (n.ownerDocument.body.contains(n)) {
                if (!n.dataset.__react_rendered) {
                    const pillValue = n.dataset.value;
                    n.dataset.__react_rendered = "1";
                    const pillComponent = (
                        <Pill value={pillValue}
                            pillConfiguration={pillConfiguration}
                            pillValuesSplitFn={props.pillValuesSplitFn}
                            disableMultipleConfigurations={props.disableMultipleConfigurations}
                        />
                    )
                    ReactDOM.render(pillComponent, n);
                    needsRefresh = true;
                }
                return true;
            } else {
                if (n.dataset.__react_rendered) {
                    ReactDOM.unmountComponentAtNode(n);
                }
                return false;
            }
        });

        if (needsRefresh) {
            setTimeout(() => {
                editor.refresh();
            }, 1000);
        }
    }

    function replaceExpressionsWithPlaceholders(editor: any, loc: any) {
        const cursor = editor.getSearchCursor(ExpressionRegex, loc, { multiline: 'disable' })

        while (cursor.findNext()) {
            const markers = editor.findMarks(cursor.from(), cursor.to())

            if (markers.length === 0) {
                const from = cursor.from()
                const to = cursor.to()

                // extract if checkbox is selected or not
                const match = editor.getRange(from, to)
                const groups = ExpressionRegex.exec(match)


                const node = document.createElement('div');
                node.setAttribute('class', 'uxp-pill-input-pill-container')
                node.setAttribute('data-value', groups[1]);
                node.textContent = groups[1]
                node.addEventListener('click', () => {
                    editor.focus()
                    editor.setCursor({ line: loc.line, ch: to.ch + 1 })
                })

                nodesRef.current.push(node)
                editor.markText(from, to, { replacedWith: node });
            }
        }
    }

    function focusInput() {
        const editor = editorInstanceRef.current

        console.log('editor ', editor);

        if (editor) {
            const doc = editor.getDoc()
            const cursor = doc.getCursor()

            if (!isCursorAtEndRef.current) {
                doc.replaceRange('', { line: cursor.line, ch: cursor.ch })
                isCursorAtEndRef.current = true
            }
        }
    }

    function insertAtCursor(value: string) {
        const editor = editorInstanceRef.current
        if (editor) {
            const selection = editor.getSelection()
            if (selection.length > 0) {
                editor.replaceSelection(value)
            }
            else {
                const doc = editor.getDoc()
                const cursor = doc.getCursor()
                doc.replaceRange(value, { line: cursor.line, ch: cursor.ch })
            }
        }
    }


    function handleFocus(editor: CodeMirrorEditor, event: Event) {
        focusInput()
        if (onFocus) onFocus(editor, event)
    }

    function handleBlur() {
        isCursorAtEndRef.current = false
        if (onBlur) onBlur()
    }

    return <div className={classNames("uxp-pill-input-container", { 'has-content': value?.trim()?.length > 0 })}>
        <div className="uxp-pill-input" ref={inputContainerRef}
            onClick={e => {
                if (inputContainerRef.current.contains(e.target)) return
                focusInput()
            }}
        >
            <CodeMirror
                value={value}
                options={{
                    theme: 'plain',
                    direction: 'ltr',
                    lineWrapping: true,
                    placeholder: props.placeholder
                }}
                onBeforeChange={handleBeforeChange}
                onChange={handleChange}
                onUpdate={handleUpdate}
                editorDidMount={handleOnMount}
                className='uxp-pill-input-code-mirror'
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
        </div>
    </div>

})


interface IPillProps {
    value: string,
    pillConfiguration: IPillConfiguration,
    draggable?: boolean,
    className?: string,
    onClick?: () => void,
    pillValuesSplitFn?: (value: string) => string[]; // [type, ...rest]
    disableMultipleConfigurations?: boolean,
}

export const Pill: React.FunctionComponent<IPillProps> = (props) => {
    const { value, pillValuesSplitFn, pillConfiguration, draggable, className, onClick } = props

    console.log("pill value", value, pillValuesSplitFn ? pillValuesSplitFn(value) : 'hello')

    // get the configurations 
    let parts;
    // get the configuration 
    let pillValue: string | JSX.Element = (value as string)
    let pillIcon: string = 'fas tag'
    let pillBG: string = '#424242'
    let pillColor: string = 'white'

    let config: IPillConfig;

    if (props.disableMultipleConfigurations) {
        config = pillConfiguration["config"];
    } else {
        if (pillValuesSplitFn) {
            parts = pillValuesSplitFn(value)
        } else {
            parts = value.split(".")
        }

        if (parts.length === 0) return null

        const type = parts[0]
        config = pillConfiguration[type]
    }


    if (config) {
        const { valueIndex, icon, backgroundColor, color, renderValue } = config

        console.log("renderValue", renderValue)

        if (renderValue) {
            pillValue = renderValue(value)
        } else {
            pillValue = parts ? parts[valueIndex] : value
        }

        pillBG = typeof backgroundColor == 'string' ? backgroundColor : backgroundColor(value)
        pillIcon = typeof icon == 'string' ? icon : icon(value)
        pillColor = typeof color == 'string' ? color : color(value)

        console.log('Configurations ', pillBG, pillIcon, pillColor);

    }

    else {
        console.log("Unable to find a configuration for the pill. value: ", value)
    }


    if (!hasValue(pillBG)) pillBG = '#424242'
    if (!hasValue(pillColor)) pillColor = 'white'
    if (!hasValue(pillIcon)) pillIcon = 'fas tag'

    function renderIcon() {
        let fontAwesomeRegex = /fa[bdlrs]\s[a-zA-Z]+/g
        if (pillIcon && fontAwesomeRegex.test(pillIcon.trim())) {
            let parts = pillIcon.split(" ")
            return <FontAwesomeIcon icon={parts as IconProp} />
        }
        if (typeof pillIcon == 'string' && !!pillIcon) {
            return <div className='pill-icon-url' style={{ backgroundImage: `url(${pillIcon})` }} />;
        }
        return null
    }

    let styles: React.CSSProperties = {
        backgroundColor: pillBG,
        borderColor: pillBG,
        color: pillColor
    }


    function onDragStart(e: React.DragEvent<HTMLElement>) {
        e.dataTransfer.setData('text/plain', '#{' + value + '}')
    }

    function handleClick() {
        if (onClick) onClick()
    }

    return <div
        className={classNames('uxp-pill-input-pill', className || '')}
        style={styles}
        draggable={draggable}
        onDragStart={onDragStart}
        onClick={handleClick}
    >
        <div className='uxp-pill-icon'>
            {renderIcon()}
        </div>
        <div className="uxp-pill-text">{pillValue}</div>
    </div>
}

export default PillInput